/**
 * URL Parameters Parser
 *
 * Parses and validates URL parameters for PouchPay payment flow
 * Similar to Fonbnk's URL parameter system
 */

export interface PaymentUrlParams {
  // Required parameters
  source: string;
  signature: string;

  // Transaction parameters
  type?: 'onramp' | 'offramp';
  network?: string;
  asset?: string;
  amount?: number;
  currency?: 'local' | 'crypto';
  countryCode?: string;
  walletAddress?: string;

  // Authentication
  at?: string; // Access token (shared-KYC JWT)

  // Screen control
  freezeAmount?: boolean;
  freezeWallet?: boolean;
  quoteId?: string;

  // Customization
  title?: string;
  logoUrl?: string;
  brandColor?: string;
  successUrl?: string;
  callbackUrl?: string;

  // Pre-fill data
  email?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
}

/**
 * Parse URL search params into typed PaymentUrlParams object
 */
export function parsePaymentUrlParams(searchParams: URLSearchParams): PaymentUrlParams {
  const params: PaymentUrlParams = {
    source: searchParams.get('source') || '',
    signature: searchParams.get('signature') || '',
  };

  // Transaction params
  const type = searchParams.get('type');
  if (type === 'onramp' || type === 'offramp') {
    params.type = type;
  }

  params.network = searchParams.get('network') || undefined;
  params.asset = searchParams.get('asset') || undefined;

  const amount = searchParams.get('amount');
  if (amount) {
    params.amount = parseFloat(amount);
  }

  const currency = searchParams.get('currency');
  if (currency === 'local' || currency === 'crypto') {
    params.currency = currency;
  }

  params.countryCode = searchParams.get('countryCode') || undefined;
  params.walletAddress = searchParams.get('walletAddress') || undefined;

  // Authentication
  params.at = searchParams.get('at') || undefined;

  // Screen control
  params.freezeAmount = searchParams.get('freezeAmount') === 'true';
  params.freezeWallet = searchParams.get('freezeWallet') === 'true';
  params.quoteId = searchParams.get('quoteId') || undefined;

  // Customization
  params.title = searchParams.get('title') || undefined;
  params.logoUrl = searchParams.get('logoUrl') || undefined;
  params.brandColor = searchParams.get('brandColor') || undefined;
  params.successUrl = searchParams.get('successUrl') || undefined;
  params.callbackUrl = searchParams.get('callbackUrl') || undefined;

  // Pre-fill data
  params.email = searchParams.get('email') || undefined;
  params.firstName = searchParams.get('firstName') || undefined;
  params.lastName = searchParams.get('lastName') || undefined;
  params.phoneNumber = searchParams.get('phoneNumber') || undefined;

  return params;
}

/**
 * Validate required params are present
 */
export function validateRequiredParams(params: PaymentUrlParams): { valid: boolean; error?: string } {
  if (!params.source) {
    return { valid: false, error: 'Missing required parameter: source' };
  }

  if (!params.signature) {
    return { valid: false, error: 'Missing required parameter: signature' };
  }

  return { valid: true };
}

/**
 * Determine which page to start on based on provided params
 * Screen skipping logic
 */
export function determineStartPage(params: PaymentUrlParams): string {
  // Fast-track: All params provided + auth token
  if (
    params.at &&
    params.amount &&
    params.walletAddress &&
    params.quoteId &&
    params.type &&
    params.network &&
    params.asset
  ) {
    return '/pay/fast-track';
  }

  // Skip to review: Has auth, amount, wallet, but no quote
  if (
    params.at &&
    params.amount &&
    params.walletAddress &&
    params.type &&
    params.network &&
    params.asset
  ) {
    return '/pay/review';
  }

  // Skip to auth: Has amount and wallet, but no auth
  if (
    params.amount &&
    params.walletAddress &&
    params.type &&
    params.network &&
    params.asset
  ) {
    return '/pay/auth';
  }

  // Skip to wallet: Has amount but no wallet
  if (
    params.amount &&
    params.type &&
    params.network &&
    params.asset
  ) {
    return '/pay/wallet';
  }

  // Start at amount page (default)
  return '/pay';
}

/**
 * Check if user can edit amount
 */
export function canEditAmount(params: PaymentUrlParams): boolean {
  return !params.freezeAmount;
}

/**
 * Check if user can edit wallet
 */
export function canEditWallet(params: PaymentUrlParams): boolean {
  return !params.freezeWallet;
}

/**
 * Build URL with params (for generating payment links)
 */
export function buildPaymentUrl(
  baseUrl: string,
  params: Partial<PaymentUrlParams>
): string {
  const url = new URL(baseUrl);

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      url.searchParams.set(key, String(value));
    }
  });

  return url.toString();
}

/**
 * Get currency for country code
 */
export function getCurrencyForCountry(countryCode: string): string {
  const currencyMap: Record<string, string> = {
    NG: 'NGN',
    KE: 'KES',
    GH: 'GHS',
    ZA: 'ZAR',
    UG: 'UGX'
  };

  return currencyMap[countryCode] || 'USD';
}

/**
 * Validate wallet address format (basic validation)
 */
export function isValidWalletAddress(address: string, network?: string): boolean {
  if (!address) return false;

  // EVM addresses (ETH, POLYGON, etc.)
  if (network?.includes('ETH') || network?.includes('POLYGON') || network?.includes('BASE')) {
    return /^0x[a-fA-F0-9]{40}$/.test(address);
  }

  // TRON addresses
  if (network?.includes('TRC') || network?.includes('TRON')) {
    return /^T[a-zA-Z0-9]{33}$/.test(address);
  }

  // Solana addresses
  if (network?.includes('SOL')) {
    return /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(address);
  }

  // Generic validation - at least 20 characters
  return address.length >= 20;
}
